/**
 * Assign square (screens 07, 07b), for admins. Names the square and the game,
 * shows who holds it now with Release square, then a search over GUESTS IN
 * THIS POOL and MEMBERS with each one's count on this grid ("3 of 5 squares
 * · at limit"), and Assign to. "+ Add a guest" saves a guest to the pool and
 * assigns the square in one go. Counts and limits are the server's.
 */
import {
  useSquaresAssigneeList,
  useSquaresGuestCreate,
  useSquaresSquareAssign,
} from "@sportsmet/contest-sdk/private";
import {
  Button,
  confirmDestructive,
  Dialog,
  Input,
  Radio,
  Skeleton,
} from "@sportsmet/ui";
import { type ReactNode, useEffect, useState } from "react";

import {
  confirmSquaresDiscard,
  runSquaresMutation,
  useRefreshSquaresPoolLine,
} from "./squaresActions";
import {
  EMPTY_SQUARES_GUEST_DRAFT,
  isSquaresGuestDraftDirty,
  isSquaresGuestDraftReady,
  SquaresAddGuestForm,
  type typeSquaresGuestDraft,
} from "./SquaresAddGuestForm";
import {
  SQUARES_ADD_GRID_CANCEL,
  SQUARES_ADD_GUEST,
  SQUARES_ASSIGN_CURRENT,
  SQUARES_ASSIGN_GUESTS_HEADING,
  SQUARES_ASSIGN_MEMBERS_HEADING,
  SQUARES_ASSIGN_NONE_FOUND,
  SQUARES_ASSIGN_NOT_JOINED,
  SQUARES_ASSIGN_PICK_SOMEONE,
  SQUARES_ASSIGN_RELEASE,
  SQUARES_ASSIGN_SEARCH,
  SQUARES_ASSIGN_TITLE,
  SQUARES_GUEST_BACK,
  SQUARES_GUEST_SUBMIT,
  SQUARES_RELEASE_CONFIRM_BODY,
  SQUARES_RELEASE_CONFIRM_LABEL,
  SQUARES_RELEASE_CONFIRM_TITLE,
  squaresAssignCountLine,
  squaresAssignPlace,
  squaresAssignTo,
} from "./squaresCopy";
import type { typeSquaresBoardCell } from "./squaresModel";
import { squaresMatchesSearch } from "./squaresModel";
import { SquaresGuestTag } from "./SquaresWinningCard";

const GUEST_PREFIX = "guest:";
const MEMBER_PREFIX = "member:";

export type typeSquaresAssignSheetProps = {
  poolId: string;
  gridId: string;
  /** Null keeps the sheet closed. */
  cell: typeSquaresBoardCell | null;
  gameLabel: string;
  onClose: () => void;
};

export const SquaresAssignSheet = (
  props: typeSquaresAssignSheetProps,
): ReactNode => {
  const isOpen = props.cell !== null;
  const refreshPoolLine = useRefreshSquaresPoolLine(props.poolId);
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState<string | null>(null);
  const [isGuestMode, setGuestMode] = useState(false);
  const [guest, setGuest] = useState<typeSquaresGuestDraft>(
    EMPTY_SQUARES_GUEST_DRAFT,
  );
  const [busy, setBusy] = useState<"assign" | "release" | "guest" | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setQuery("");
    setPicked(null);
    setGuestMode(false);
    setGuest(EMPTY_SQUARES_GUEST_DRAFT);
  }, [isOpen, props.cell?.squareKey]);

  const assignees = useSquaresAssigneeList(
    { poolId: props.poolId, gridId: props.gridId },
    { enabled: isOpen },
  );
  const assign = useSquaresSquareAssign();
  const guestCreate = useSquaresGuestCreate();
  const data = assignees.data?.data ?? null;
  const guests = (data?.guests ?? []).filter((item) =>
    squaresMatchesSearch(item.displayName, query),
  );
  const members = (data?.members ?? []).filter((item) =>
    squaresMatchesSearch(item.displayName, query),
  );
  const holder = props.cell?.square ?? null;

  const pickedName = ((): string | null => {
    if (picked === null) return null;
    if (picked.startsWith(GUEST_PREFIX)) {
      const id = picked.slice(GUEST_PREFIX.length);
      return (
        data?.guests.find((item) => item.guestId === id)?.displayName ?? null
      );
    }
    const id = picked.slice(MEMBER_PREFIX.length);
    return (
      data?.members.find((item) => item.userId === id)?.displayName ?? null
    );
  })();

  async function assignTo(
    assignee: { ownerKind: "member" | "guest"; ownerId: string } | null,
    kind: "assign" | "release",
  ): Promise<void> {
    if (props.cell === null) return;
    setBusy(kind);
    const result = await runSquaresMutation(
      () =>
        assign.mutateAsync({
          poolId: props.poolId,
          gridId: props.gridId,
          squareKey: props.cell?.squareKey ?? "",
          assignee,
        }),
      refreshPoolLine,
    );
    setBusy(null);
    if (result !== null) props.onClose();
  }

  async function submitAssign(): Promise<void> {
    if (picked === null) return;
    const isGuest = picked.startsWith(GUEST_PREFIX);
    await assignTo(
      {
        ownerKind: isGuest ? "guest" : "member",
        ownerId: picked.slice(
          isGuest ? GUEST_PREFIX.length : MEMBER_PREFIX.length,
        ),
      },
      "assign",
    );
  }

  async function submitGuest(): Promise<void> {
    if (!isSquaresGuestDraftReady(guest)) return;
    setBusy("guest");
    const created = await runSquaresMutation(() =>
      guestCreate.mutateAsync({
        poolId: props.poolId,
        firstName: guest.firstName.trim(),
        lastName: guest.lastName.trim(),
        initials: guest.initials,
      }),
    );
    const guestId = created?.data?.guest.guestId ?? null;
    if (guestId === null) {
      setBusy(null);
      return;
    }
    setBusy(null);
    await assignTo({ ownerKind: "guest", ownerId: guestId }, "assign");
  }

  async function release(): Promise<void> {
    const isConfirmed = await confirmDestructive({
      title: SQUARES_RELEASE_CONFIRM_TITLE,
      body: SQUARES_RELEASE_CONFIRM_BODY,
      confirmLabel: SQUARES_RELEASE_CONFIRM_LABEL,
    });
    if (isConfirmed) await assignTo(null, "release");
  }

  const isDirty = isGuestMode && isSquaresGuestDraftDirty(guest);

  const footer = isGuestMode ? (
    <>
      <Button
        variant="secondary"
        onClick={() => {
          setGuestMode(false);
        }}
      >
        {SQUARES_GUEST_BACK}
      </Button>
      <Button
        disabled={!isSquaresGuestDraftReady(guest)}
        loading={busy === "guest" || (busy === "assign" && isGuestMode)}
        data-testid="squares-guest-submit"
        onClick={() => {
          void submitGuest();
        }}
      >
        {SQUARES_GUEST_SUBMIT}
      </Button>
    </>
  ) : (
    <>
      <Button variant="secondary" onClick={props.onClose}>
        {SQUARES_ADD_GRID_CANCEL}
      </Button>
      <Button
        disabled={picked === null}
        loading={busy === "assign"}
        data-testid="squares-assign-submit"
        onClick={() => {
          void submitAssign();
        }}
      >
        {pickedName === null
          ? SQUARES_ASSIGN_PICK_SOMEONE
          : squaresAssignTo(pickedName)}
      </Button>
    </>
  );

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) props.onClose();
      }}
      title={SQUARES_ASSIGN_TITLE}
      description={
        props.cell === null
          ? undefined
          : squaresAssignPlace({
              row: props.cell.row,
              col: props.cell.col,
              gameLabel: props.gameLabel,
            })
      }
      autoFocus={isGuestMode}
      isDirty={isDirty}
      onRequestClose={() => {
        void (async () => {
          if (await confirmSquaresDiscard("guest")) props.onClose();
        })();
      }}
      footer={footer}
    >
      <div className="flex flex-col gap-4" data-testid="squares-assign-sheet">
        {holder === null ? null : (
          <div
            className="flex items-center justify-between gap-3 rounded-md border border-hairline p-3"
            data-testid="squares-assign-holder"
          >
            <div className="flex min-w-0 flex-col gap-1">
              <span className="text-caption text-ink-secondary">
                {SQUARES_ASSIGN_CURRENT}
              </span>
              <span className="flex items-center gap-2 text-body-strong text-ink">
                {holder.displayName}
                {holder.isGuest ? <SquaresGuestTag /> : null}
              </span>
            </div>
            <Button
              variant="secondary"
              size="sm"
              loading={busy === "release"}
              data-testid="squares-assign-release"
              onClick={() => {
                void release();
              }}
            >
              {SQUARES_ASSIGN_RELEASE}
            </Button>
          </div>
        )}
        {isGuestMode ? (
          <SquaresAddGuestForm value={guest} onChange={setGuest} />
        ) : (
          <>
            <Input
              label={SQUARES_ASSIGN_SEARCH}
              type="search"
              value={query}
              data-testid="squares-assign-search"
              onChange={(event) => {
                setQuery(event.target.value);
              }}
            />
            {assignees.isLoading ? (
              <div className="flex flex-col gap-2">
                <Skeleton className="h-10" />
                <Skeleton className="h-10" />
              </div>
            ) : (
              <>
                <section className="flex flex-col gap-2">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="text-caption font-semibold uppercase text-ink-secondary">
                      {SQUARES_ASSIGN_GUESTS_HEADING}
                    </h3>
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid="squares-add-guest"
                      onClick={() => {
                        setGuestMode(true);
                      }}
                    >
                      {SQUARES_ADD_GUEST}
                    </Button>
                  </div>
                  {guests.length === 0 ? null : (
                    <Radio
                      label={SQUARES_ASSIGN_GUESTS_HEADING}
                      labelHidden
                      value={picked ?? ""}
                      onValueChange={setPicked}
                      items={guests.map((item) => ({
                        value: `${GUEST_PREFIX}${item.guestId}`,
                        label: `${item.displayName} (${item.initials})`,
                        description: squaresAssignCountLine({
                          count: item.squareCount,
                          max: data?.maxPerMember ?? null,
                          isAtLimit: item.isAtLimit,
                        }),
                        disabled: item.isAtLimit,
                      }))}
                    />
                  )}
                </section>
                <section className="flex flex-col gap-2">
                  <h3 className="text-caption font-semibold uppercase text-ink-secondary">
                    {SQUARES_ASSIGN_MEMBERS_HEADING}
                  </h3>
                  {members.length === 0 && guests.length === 0 ? (
                    <p className="text-body text-ink-secondary">
                      {SQUARES_ASSIGN_NONE_FOUND}
                    </p>
                  ) : (
                    <Radio
                      label={SQUARES_ASSIGN_MEMBERS_HEADING}
                      labelHidden
                      value={picked ?? ""}
                      onValueChange={setPicked}
                      items={members.map((item) => ({
                        value: `${MEMBER_PREFIX}${item.userId}`,
                        label: item.displayName,
                        description:
                          item.squareCount === 0
                            ? SQUARES_ASSIGN_NOT_JOINED
                            : squaresAssignCountLine({
                                count: item.squareCount,
                                max: data?.maxPerMember ?? null,
                                isAtLimit: item.isAtLimit,
                              }),
                        disabled: item.isAtLimit,
                      }))}
                    />
                  )}
                </section>
              </>
            )}
          </>
        )}
      </div>
    </Dialog>
  );
};
